import type { ThemeClasses, ViewType } from "../types";
import type { TFunction } from "i18next";
import { Button, Combobox, ViewToggle } from "../../../../../../components/ui";
import { useAllDevices } from "../../../../../../features/devices/queries";
import { useStatuses } from "../../../../../../features/catalogs/queries";
import { useMemo, useState } from "react";
import { getStatusOptionsForEntity } from "../../../../../../utils/getStatusEntity";

interface TripFiltersProps {
  themeClasses: ThemeClasses;
  selectedStatus: string;
  searchQuery: string;
  selectedDeviceId: string;
  dateFrom: string;
  dateTo: string;
  onStatusChange: (status: string) => void;
  onSearchChange: (search: string) => void;
  onDeviceChange: (deviceId: string) => void;
  onDateFromChange: (date: string) => void;
  onDateToChange: (date: string) => void;
  onClearFilters: () => void;
  onViewChange: (view: ViewType) => void;
  selectedView: ViewType;
  t: TFunction;
}

export function TripFilters({
  themeClasses,
  selectedStatus,
  searchQuery,
  selectedDeviceId,
  dateFrom,
  dateTo,
  onStatusChange,
  onSearchChange,
  onDeviceChange,
  onDateFromChange,
  onDateToChange,
  onClearFilters,
  onViewChange,
  selectedView,
  t,
}: TripFiltersProps) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { data: devices = [] } = useAllDevices();
  const { data: statuses = [] } = useStatuses();

  // Get status options for trips (ACTIVE, INACTIVE)
  const statusOptions = useMemo(
    () => getStatusOptionsForEntity(statuses, "trips", t),
    [statuses, t]
  );

  const deviceOptions = useMemo(
    () => devices.map((d) => {
      const vehicleName = d.vehicle ? d.vehicle?.name : "";
      const trailerName = d.remolque ? d.remolque?.name : "";
      const nameDevice = vehicleName || trailerName ? d.identifier : d.name;
      return {
        value: d.id,
        label: `${nameDevice} ${vehicleName ? `(${vehicleName})` : ""} ${trailerName ? `(${trailerName})` : ""}`.trim(),
      };
    }),
    [devices]
  );

  const activeFilterCount = [selectedStatus, selectedDeviceId, dateFrom, dateTo].filter(Boolean).length;

  const hasActiveFilters = selectedStatus || searchQuery || selectedDeviceId || dateFrom || dateTo;

  const fieldBaseClass = `w-full h-10 bg-app border ${themeClasses.cardBorder} rounded-lg ${
    themeClasses.text
  } placeholder:text-content-faint focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm`;

  return (
    <div
      className={`${themeClasses.bg} border-b ${themeClasses.border} px-4 sm:px-6 pb-4 sm:pb-6`}
    >
      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-row sm:flex-wrap sm:items-end xl:flex-nowrap">
        {/* Status filter */}
        <div className={`w-full sm:flex-1 sm:min-w-[150px] xl:max-w-[170px] ${filtersOpen ? "" : "max-sm:hidden"} max-sm:order-3`}>
          <label className={`block text-[11px] mb-1 ${themeClasses.textSecondary}`}>
            {t("trips.filterStatus")}
          </label>
          <Combobox
            options={statusOptions}
            placeholder={t("trips.filterStatus")}
            value={statusOptions.find((opt) => opt.value === selectedStatus) || null}
            onChange={(option: any) => onStatusChange(option?.value || "")}
            isSearchable
            isClearable
            noOptionsMessage={() => t("trips.filters.noStatusesAvailable")}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>

        {/* Search */}
        <div className="w-full max-sm:col-span-2 max-sm:order-1 sm:flex-1 sm:min-w-[180px] xl:max-w-[240px]">
          <label className={`block text-[11px] mb-1 ${themeClasses.textSecondary}`}>
            {t("trips.searchPlaceholder")}
          </label>
          <div className="relative">
            <i
              className={`ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 ${themeClasses.textSecondary} text-sm`}
            ></i>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={t("trips.searchPlaceholder")}
              className={`${fieldBaseClass} pl-9`}
            />
          </div>
        </div>

        {/* Device filter */}
        <div className={`w-full sm:flex-1 sm:min-w-[150px] xl:max-w-[220px] ${filtersOpen ? "" : "max-sm:hidden"} max-sm:order-3`}>
          <label className={`block text-[11px] mb-1 ${themeClasses.textSecondary}`}>
            {t("trips.filters.device")}
          </label>
          <Combobox
            options={deviceOptions}
            placeholder={t("trips.filters.devicePlaceholder")}
            value={deviceOptions.find((opt) => opt.value === selectedDeviceId) || null}
            onChange={(option: any) => onDeviceChange(option?.value || "")}
            isSearchable
            isClearable
            noOptionsMessage={() => t("trips.filters.noDevicesAvailable")}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>

        {/* Date from */}
        <div className={`w-full sm:flex-1 sm:min-w-[140px] xl:max-w-[160px] ${filtersOpen ? "" : "max-sm:hidden"} max-sm:order-3`}>
          <label className={`block text-[11px] mb-1 ${themeClasses.textSecondary}`}>
            {t("trips.filters.dateFrom")}
          </label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => onDateFromChange(e.target.value)}
            className={`${fieldBaseClass} px-3`}
            title={t("trips.filters.dateFrom")}
          />
        </div>

        {/* Date to */}
        <div className={`w-full sm:flex-1 sm:min-w-[140px] xl:max-w-[160px] ${filtersOpen ? "" : "max-sm:hidden"} max-sm:order-3`}>
          <label className={`block text-[11px] mb-1 ${themeClasses.textSecondary}`}>
            {t("trips.filters.dateTo")}
          </label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => onDateToChange(e.target.value)}
            min={dateFrom || undefined}
            className={`${fieldBaseClass} px-3`}
            title={t("trips.filters.dateTo")}
          />
        </div>

        {/* Action buttons (clear + view toggle) */}
        <div className="flex items-center justify-end gap-2 shrink-0 max-sm:col-span-2 max-sm:order-2">
          <Button
            variant="secondary"
            size="sm"
            className="sm:hidden"
            onClick={() => setFiltersOpen((open) => !open)}
            aria-expanded={filtersOpen}
            icon={<i className="ri-filter-3-line" />}
          >
            {t("trips.filters.title", "Filtros")}
            {activeFilterCount > 0 && (
              <span className="ml-1 rounded-full bg-primary-500 px-1.5 text-[11px] leading-4 text-on-primary">{activeFilterCount}</span>
            )}
          </Button>
          {hasActiveFilters && (
            <button
              onClick={onClearFilters}
              className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors whitespace-nowrap bg-error/15 text-error hover:bg-error/25`}
              title={t("trips.filters.clearAll")}
            >
              <i className="ri-filter-off-line mr-1"></i>
              {t("trips.filters.clearAll")}
            </button>
          )}
          <ViewToggle value={selectedView} onChange={onViewChange} />
        </div>
      </div>
    </div>
  );
}
